import type { BogglePosition } from '~/boggle/engine/standard/types'
import type { BoardLayout } from '~/boggle/ui/run/pixi/usePixiRuntime'
import type { TileShapeKind } from '~/boggle/ui/run/pixi/tileShapeDraw'

export type BoggleBoardSurfaceSnapshot = {
  rect: DOMRectReadOnly
  layout: BoardLayout
  rows: number
  cols: number
  rendererWidth: number
  rendererHeight: number
}

export type BoggleAmbientBoardEffect = {
  type: 'tilePressPulse'
  position: BogglePosition
  tileShape: TileShapeKind
  startedAtMs: number
}

type BridgeState = {
  snapshot: BoggleBoardSurfaceSnapshot | null
  effects: BoggleAmbientBoardEffect[]
}

const bridgeState: BridgeState = {
  snapshot: null,
  effects: [],
}

let boardEffectsChangeListener: (() => void) | null = null

export function setBoggleAmbientBoardEffectsListener(listener: (() => void) | null) {
  boardEffectsChangeListener = listener
}

export function setBoggleBoardSurfaceSnapshot(snapshot: BoggleBoardSurfaceSnapshot | null) {
  bridgeState.snapshot = snapshot
}

export function pushBoggleAmbientBoardEffect(effect: BoggleAmbientBoardEffect) {
  bridgeState.effects.push(effect)
  boardEffectsChangeListener?.()
}

export function clearBoggleAmbientBoardEffects() {
  bridgeState.effects = []
}

export function setBoggleAmbientBoardEffects(effects: BoggleAmbientBoardEffect[]) {
  bridgeState.effects = effects
}

export function resetBoggleBoardSurfaceBridge() {
  bridgeState.snapshot = null
  bridgeState.effects = []
}

export function getBoggleBoardSurfaceBridgeState(): Readonly<BridgeState> {
  return bridgeState
}

function boardLocalToViewport(
  snapshot: BoggleBoardSurfaceSnapshot,
  localX: number,
  localY: number,
): { x: number; y: number } {
  const { rect, rendererWidth, rendererHeight } = snapshot
  if (rect.width <= 0 || rect.height <= 0 || rendererWidth <= 0 || rendererHeight <= 0) {
    return { x: rect.left, y: rect.top }
  }

  return {
    x: rect.left + (localX / rendererWidth) * rect.width,
    y: rect.top + (localY / rendererHeight) * rect.height,
  }
}

function viewportToAmbientCanvas(
  viewportX: number,
  viewportY: number,
  ambientHostRect: DOMRectReadOnly,
  ambientCanvasW: number,
  ambientCanvasH: number,
): { x: number; y: number } {
  if (
    ambientHostRect.width <= 0 ||
    ambientHostRect.height <= 0 ||
    ambientCanvasW <= 0 ||
    ambientCanvasH <= 0
  ) {
    return { x: 0, y: 0 }
  }

  return {
    x: ((viewportX - ambientHostRect.left) / ambientHostRect.width) * ambientCanvasW,
    y: ((viewportY - ambientHostRect.top) / ambientHostRect.height) * ambientCanvasH,
  }
}

export function boardLocalToAmbient(
  snapshot: BoggleBoardSurfaceSnapshot,
  ambientHostRect: DOMRectReadOnly,
  ambientCanvasW: number,
  ambientCanvasH: number,
  localX: number,
  localY: number,
): { x: number; y: number } {
  const viewport = boardLocalToViewport(snapshot, localX, localY)
  return viewportToAmbientCanvas(
    viewport.x,
    viewport.y,
    ambientHostRect,
    ambientCanvasW,
    ambientCanvasH,
  )
}

export function boardLocalLengthToAmbient(
  snapshot: BoggleBoardSurfaceSnapshot,
  ambientHostRect: DOMRectReadOnly,
  ambientCanvasW: number,
  ambientCanvasH: number,
  localLength: number,
): number {
  const origin = boardLocalToAmbient(
    snapshot,
    ambientHostRect,
    ambientCanvasW,
    ambientCanvasH,
    0,
    0,
  )
  const offset = boardLocalToAmbient(
    snapshot,
    ambientHostRect,
    ambientCanvasW,
    ambientCanvasH,
    localLength,
    0,
  )
  return Math.hypot(offset.x - origin.x, offset.y - origin.y)
}

export function boardPositionCenter(
  layout: BoardLayout,
  position: BogglePosition,
  visualRow = position.row,
) {
  return {
    x: layout.originX + position.col * layout.cellSize + layout.cellSize / 2,
    y: layout.originY + visualRow * layout.cellSize + layout.cellSize / 2,
  }
}
